import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Icon } from "@iconify/react";
import { Table, Tag, Tooltip } from "antd";

import InfoExpA from "js/components/Dashboard/InfoExpA";
import TenantActionsTable from "js/components/Tenants/TenantActionSelectTable";
import { Person, TenantReport } from "js/components/Tenants/types";
import { PaginationTable } from "js/components/types";

import "styles/components/Dashboard/TableDashboard.scss";

interface TenantReportsTablePresentationProps {
  data: TenantReport[];
  isLoading: boolean;
  pagination: PaginationTable;
  onPageChange: (page: number) => void;
  showAdvisorColumn?: boolean;
}

const TenantReportsTablePresentation: React.FC<
  TenantReportsTablePresentationProps
> = ({
  data,
  isLoading,
  pagination,
  onPageChange,
  showAdvisorColumn = false,
}) => {
  const navigate = useNavigate();
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

  const openModal = (report: TenantReport) => () => {
    if (report.expediente_azul_folder_id) {
      setIsModalVisible(true);
    }
  };

  const columns = [
    showAdvisorColumn
      ? {
          title: "Asesor",
          render: (_: any, report: TenantReport) =>
            report?.advisor?.full_name ?? "Huérfano",
          ellipsis: true,
          width: "5%",
        }
      : null,
    {
      title: "Estatus",
      dataIndex: "is_active",
      render: (is_active: boolean) => (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Tooltip title={is_active ? "Activo" : "Inactivo"}>
            <Icon
              icon={is_active ? "ion:checkmark-circle" : "ion:close-circle"}
              fontSize={20}
              color={is_active ? "#52c41a" : "#d9d9d9"}
              style={{ cursor: "default" }}
            />
          </Tooltip>
        </div>
      ),
      align: "center" as const,
      width: "6%",
    },
    {
      title: "Inquilino",
      dataIndex: "person",
      render: (_: any, report: TenantReport) => {
        // `person` is a nullable FK: a report with no TenantPerson arrives as
        // `person: null`. Guard it the same way the mobile card list does, so the
        // desktop table degrades to "Sin nombre" instead of throwing into the
        // app-level error boundary.
        const name =
          report.person?.legal_representative ?? report.person?.full_name;
        const disabled = report.status === "Created" || !report.is_active;
        const handleClick = () => {
          if (disabled) return;
          if (report.expediente_azul_folder_id) setIsModalVisible(true);
          else navigate(`/tenants/reports/${report.id}`);
        };
        return (
          <span
            onClick={handleClick}
            style={{
              fontWeight: disabled ? 400 : 600,
              cursor: disabled ? "not-allowed" : "pointer",
              color: "#000",
              transition: "color 0.2s ease",
            }}
            onMouseEnter={(e) => {
              if (!disabled) {
                e.currentTarget.style.color = "#105ef8";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = "#000";
            }}
          >
            {name || "Sin nombre"}
          </span>
        );
      },
      width: "18%",
      ellipsis: true,
    },
    {
      title: "Folio",
      dataIndex: "expediente_azul_folder_id",
      width: "8%",
      ellipsis: true,
      render: (_: any, record: any) =>
        record.expediente_azul_folder_id || record.multiexpediente_folio || "-",
    },
    {
      title: "Correo",
      dataIndex: "person",
      ellipsis: true,
      render: (person: Person | null) => person?.email || "N/A",
      width: "15%",
    },
    {
      title: "Tipo Persona",
      dataIndex: "legal_type",
      align: "center" as const,
      render: (legal_type: string) => {
        const isMoral = legal_type === "Legal";
        const label = isMoral ? "Moral" : "Física";
        const borderColor = isMoral ? "#1677ff" : "#52c41a";
        return (
          <Tag
            bordered
            style={{
              backgroundColor: "transparent",
              borderColor,
              color: borderColor,
            }}
          >
            {label}
          </Tag>
        );
      },
      width: "8%",
    },
    {
      title: "Fase",
      dataIndex: "status",
      render: (status: string) => {
        switch (status) {
          case "Created":
            return "Iniciado";
          case "Payment in Progress":
            return "Pago Pendiente";
          case "In Progress":
            return "En progreso";
          case "Completed":
            return "Completado";
          default:
            return "Estado Desconocido";
        }
      },
      width: "12%",
      align: "center" as const,
    },
    {
      title: "Fecha de Creación",
      dataIndex: "created_at",
      render: (date: string) =>
        new Date(date).toLocaleDateString("es-ES", {
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
      width: "13%",
      align: "center" as const,
    },
    {
      title: "Acciones",
      dataIndex: "actions",
      render: (_: any, report: TenantReport) => (
        <TenantActionsTable
          report={report}
          openModal={openModal(report)}
        />
      ),
      align: "center" as const,
      width: "13%",
    },
  ].filter((column) => column !== null);

  const handleTableChange = (newPagination: any) => {
    onPageChange(newPagination.current || 1);
  };

  const headerCell = (props: any) => (
    <th
      {...props}
      style={{
        textAlign:
          props.children.includes("Acciones") ||
          props.children.includes("Fecha de Creación") ||
          props.children.includes("Estatus") ||
          props.children.includes("Tipo Persona") ||
          props.children.includes("Fase")
            ? "center"
            : undefined,
      }}
    />
  );

  return (
    <>
      <div style={{ overflowX: "auto" }}>
        <Table
          size={"small"}
          columns={columns}
          rowKey={(record) => record.id}
          dataSource={data}
          pagination={pagination}
          loading={isLoading}
          onRow={(record) => ({
            onClick: () => {
              if (record.expediente_azul_folder_id) {
                setIsModalVisible(true);
              }
            },
          })}
          onChange={handleTableChange}
          scroll={{ x: "max-content" }}
          components={{
            header: { cell: headerCell },
          }}
        />
      </div>
      <InfoExpA
        isModalVisible={isModalVisible}
        setIsModalVisible={setIsModalVisible}
      />
    </>
  );
};
export default TenantReportsTablePresentation;
